import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import React from "react";
import { Document, Page, pdfjs } from "react-pdf";

// Bundled with the app. pdfjs-dist is a direct dependency pinned to the version react-pdf uses; the worker and
// react-pdf's pdf.js must be the same version or loading fails.
//

// While on local dev this bundle is server from local development server. However, on staging/prod we need this from the CDN.
// Due to Content Security Policy restrictions, we cannot directly start a worker from a CDN-hosted script.
// In order to work around this, we fetch the worker script ourselves and start it from a blob URL.
// Not in dev, though: Vite adds root-relative imports to the script it serves, which can't resolve from a blob URL.
const startWorker = async () => {
    if (import.meta.env.DEV) {
        return pdfjs.PDFWorker.create({
            port: new Worker(new URL(pdfWorkerUrl, window.location.href), { type: "module" }),
        });
    }
    const response = await fetch(pdfWorkerUrl);
    if (!response.ok) throw new Error(`Couldn't load the PDF worker (${response.status})`);
    const code = new Blob([await response.text()], { type: "text/javascript" });
    return pdfjs.PDFWorker.create({ port: new Worker(URL.createObjectURL(code), { type: "module" }) });
};

// The preview brings its own worker instead of relying on pdfjs.GlobalWorkerOptions, which any other pdf.js user on
// the page can overwrite. One is started on first use and kept for the rest of the session.
let previewWorker: Promise<InstanceType<typeof pdfjs.PDFWorker>> | null = null;
const getPreviewWorker = (): Promise<InstanceType<typeof pdfjs.PDFWorker>> => {
    // A failed start is forgotten, so the next preview tries again.
    previewWorker ??= startWorker().catch((error) => {
        previewWorker = null;
        throw error;
    });
    return previewWorker.then((worker) => {
        if (!worker.destroyed) return worker;
        previewWorker = null;
        return getPreviewWorker();
    });
};

interface Props {
    blob: Blob | null;
    // Bumped for every new render, so the page list starts over instead of drawing pages the new PDF may not have.
    version: number;
}

const PreviewError: React.FC<{ message: string | null }> = ({ message }) => (
    <div className="p-6 text-sm text-red-600">
        The preview couldn't be shown. The download still works.
        {message && <div className="mt-1 text-xs text-red-500">{message}</div>}
    </div>
);

type PdfWorkerOptions = { worker: InstanceType<typeof pdfjs.PDFWorker> };

const PreviewPages: React.FC<{ blob: Blob; width: number; options: PdfWorkerOptions }> = ({ blob, width, options }) => {
    const [pageCount, setPageCount] = React.useState(0);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    return (
        <Document
            file={blob}
            options={options}
            onLoadSuccess={({ numPages }) => setPageCount(numPages)}
            onLoadError={(error) => {
                console.error("PDF preview failed to load", error);
                setErrorMessage(error.message);
            }}
            loading={null}
            error={<PreviewError message={errorMessage} />}
        >
            <div className="flex flex-col gap-3">
                {Array.from({ length: pageCount }, (_, index) => (
                    <div key={index} className="shadow-md ring-1 ring-gray-200 bg-white">
                        <Page
                            pageNumber={index + 1}
                            width={width}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            loading={null}
                        />
                    </div>
                ))}
            </div>
        </Document>
    );
};

// Shows the generated PDF as page images, which (unlike an embedded PDF) also works in phone browsers.
const PdfPagesPreview: React.FC<Props> = ({ blob, version }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState(0);
    // Kept in one object, as react-pdf reloads the document whenever its options object changes.
    const [options, setOptions] = React.useState<PdfWorkerOptions | null>(null);
    const [workerError, setWorkerError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let cancelled = false;
        getPreviewWorker().then(
            (worker) => !cancelled && setOptions({ worker }),
            (error: Error) => {
                console.error("PDF preview worker failed to start", error);
                if (!cancelled) setWorkerError(error.message);
            }
        );
        return () => {
            cancelled = true;
        };
    }, []);

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="w-full">
            {workerError !== null ? (
                <PreviewError message={workerError} />
            ) : (
                blob &&
                width > 0 &&
                options && <PreviewPages key={version} blob={blob} width={width} options={options} />
            )}
        </div>
    );
};

export default PdfPagesPreview;
